import {
  compileTemplateSpecForEditor,
  prepareTemplateSpecForSave,
  HIGHLIGHTS_BLOCKDEF_SNIPPET,
  PRODUCT_BLOCKDEF_SNIPPET,
  SYSTEM_PRODUCTS_LOOP_SNIPPET,
  resolveListDemoCount,
  resolveListMinItems,
  resolveListMaxItems,
  hasBlockdefs,
} from './templateCompileUtils';
import { resolveMockDefault } from '../ipsum/catalogMock';

export {
  compileTemplateSpecForEditor,
  prepareTemplateSpecForSave,
  HIGHLIGHTS_BLOCKDEF_SNIPPET,
  PRODUCT_BLOCKDEF_SNIPPET,
  SYSTEM_PRODUCTS_LOOP_SNIPPET,
  resolveListDemoCount,
  resolveListMinItems,
  resolveListMaxItems,
  hasBlockdefs,
};

const TEMPLATE_VERSION = 2;

export const defaultTheme = () => ({
  colors: {
    primary: '#1a1a2e',
    accent: '#e94560',
    background: '#ffffff',
    text: '#1a1a2e',
    muted: '#64748b',
  },
  fonts: {
    heading: "'Instrument Serif', Georgia, serif",
    body: "'DM Sans', system-ui, sans-serif",
  },
  spacing: {
    section: '4rem',
    gap: '1.5rem',
  },
});

export const defaultTemplateSpec = () => ({
  version: TEMPLATE_VERSION,
  html: '<main class="catalog-page">{{ block "hero" }}{{ block "products" }}{{ block "footer" }}</main>',
  css: '',
  theme: defaultTheme(),
  blocks: [
    {
      id: 'hero',
      label: 'Hero',
      html: '<section class="catalog-hero"><h1>{{ block.title }}</h1><p>{{ block.subtitle }}</p></section>',
      fields: [
        { key: 'title', type: 'text', label: 'Título', default: '' },
        { key: 'subtitle', type: 'textarea', label: 'Subtítulo', default: '' },
      ],
    },
    {
      id: 'products',
      label: 'Produtos',
      html: '<section class="catalog-products">{{ for product in block.products }}<article class="catalog-product"><h3>{{ product.title }}</h3>{{ for sku in product.skus }}{{ if sku.has_price }}<p class="price">{{ sku.label }} — {{ sku.price }}</p>{{ end }}{{ end }}</article>{{ end }}</section>',
      fields: [{ key: 'products', type: 'product_list', label: 'Produtos' }],
    },
    {
      id: 'footer',
      label: 'Rodapé',
      html: '<footer class="catalog-footer"><p>{{ system.settings.company_name }}</p></footer>',
      fields: [],
    },
  ],
});

const normalizeSpecInput = (raw) => {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    return JSON.parse(trimmed);
  }
  if (raw && typeof raw === 'object') return raw;
  return null;
};

export const parseTemplateSpec = (raw) => {
  const spec = normalizeSpecInput(raw) || defaultTemplateSpec();
  const next = {
    ...spec,
    version: spec.version || TEMPLATE_VERSION,
    theme: spec.theme || defaultTheme(),
    blocks: Array.isArray(spec.blocks) ? spec.blocks : [],
  };
  try {
    return compileTemplateSpecForEditor(next);
  } catch {
    return next;
  }
};

export const serializeTemplateSpec = (spec) => {
  const base = spec || defaultTemplateSpec();
  return JSON.stringify(base);
};

const emptyValueForField = (field) => {
  if (field.type === 'product_list' || field.type === 'item_list') {
    return [];
  }
  if (field.type === 'boolean') {
    return false;
  }
  if (field.type === 'number') {
    return 0;
  }
  return '';
};

const resolveFieldDefault = (field, blockId, usePlaceholders) => {
  if (usePlaceholders) {
    if (field.default !== undefined && field.default !== null && field.default !== '') {
      return resolveMockDefault(field.default, blockId, field.key, field.type);
    }
    return emptyValueForField(field);
  }
  if (field.default !== undefined && field.default !== null && field.default !== '') {
    const raw = field.default;
    if (typeof raw === 'string' && raw.trim().toLowerCase().startsWith('mock.')) {
      return emptyValueForField(field);
    }
    if (field.type !== 'product_list' && field.type !== 'item_list') {
      return raw;
    }
  }
  return emptyValueForField(field);
};

export const defaultInstanceDocument = (templateSpec, options = {}) => {
  if (!templateSpec) {
    return { version: TEMPLATE_VERSION, blocks: {} };
  }
  const usePlaceholders = options.usePlaceholders === true;
  const compiled = parseTemplateSpec(templateSpec);
  const blocks = {};
  (compiled.blocks || []).forEach((block) => {
    const values = {};
    (block.fields || []).forEach((field) => {
      values[field.key] = resolveFieldDefault(field, block.id, usePlaceholders);
    });
    blocks[block.id] = values;
  });
  return { version: TEMPLATE_VERSION, blocks };
};

export const parseInstanceDocument = (raw, templateSpec) => {
  const parsed = normalizeSpecInput(raw);
  const defaults = defaultInstanceDocument(templateSpec, { usePlaceholders: false });
  if (!parsed) return defaults;
  const doc = {
    version: parsed.version || TEMPLATE_VERSION,
    blocks: parsed.blocks && typeof parsed.blocks === 'object' ? parsed.blocks : {},
  };
  const allowedIds = new Set((defaults.blocks && Object.keys(defaults.blocks)) || []);
  const merged = { version: TEMPLATE_VERSION, blocks: { ...defaults.blocks } };
  Object.entries(doc.blocks).forEach(([blockId, vals]) => {
    if (!allowedIds.has(blockId)) return;
    merged.blocks[blockId] = { ...(merged.blocks[blockId] || {}), ...(vals || {}) };
  });
  return merged;
};

export const serializeInstanceDocument = (doc) => {
  const base = doc || { version: TEMPLATE_VERSION, blocks: {} };
  return JSON.stringify(base);
};
